import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiClock, FiSave } from 'react-icons/fi';
import api from '../../config/api';
import LoadingButton from '../LoadingButton';
import { appAlert } from '../../utils/appDialog';

const PRIORITIES = [
  { id: 'urgent', label: 'Urgent', hint: 'Default 30 minutes' },
  { id: 'high', label: 'High', hint: 'Default 6 hours' },
  { id: 'normal', label: 'Normal', hint: 'Default 1 day' },
  { id: 'low', label: 'Low', hint: 'Default 3 days' },
];

const UNITS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

const DEFAULT_FORM = {
  enabled: true,
  policies: {
    urgent: { value: 30, unit: 'minutes' },
    high: { value: 6, unit: 'hours' },
    normal: { value: 1, unit: 'days' },
    low: { value: 3, unit: 'days' },
  },
};

export default function SupportSlaMetadataPanel({ type }) {
  const queryClient = useQueryClient();
  const Icon = type.icon || FiClock;
  const [form, setForm] = useState(DEFAULT_FORM);

  const { data, isLoading, isError, error } = useQuery(
    ['support-sla'],
    () => api.get('/support/sla').then((r) => r.data),
    { staleTime: 30_000 }
  );

  useEffect(() => {
    if (!data) return;
    setForm({
      enabled: data.enabled !== false,
      policies: {
        urgent: { ...DEFAULT_FORM.policies.urgent, ...(data.policies?.urgent || {}) },
        high: { ...DEFAULT_FORM.policies.high, ...(data.policies?.high || {}) },
        normal: { ...DEFAULT_FORM.policies.normal, ...(data.policies?.normal || {}) },
        low: { ...DEFAULT_FORM.policies.low, ...(data.policies?.low || {}) },
      },
    });
  }, [data]);

  const saveMutation = useMutation((body) => api.put('/support/sla', body), {
    onSuccess: (res) => {
      queryClient.setQueryData(['support-sla'], res.data);
      queryClient.invalidateQueries(['support']);
      void appAlert('Support SLA targets saved.');
    },
    onError: (e) => void appAlert(e.response?.data?.error || e.message),
  });

  const updatePolicy = (id, field, value) => {
    setForm((prev) => ({
      ...prev,
      policies: {
        ...prev.policies,
        [id]: {
          ...prev.policies[id],
          [field]: field === 'value' ? value : value,
        },
      },
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    const policies = {};
    for (const p of PRIORITIES) {
      const row = form.policies[p.id] || {};
      const value = Number(row.value);
      if (!Number.isFinite(value) || value <= 0) {
        void appAlert(`${p.label}: enter a positive number.`);
        return;
      }
      policies[p.id] = { value, unit: row.unit || 'days' };
    }
    saveMutation.mutate({ enabled: form.enabled, policies });
  };

  return (
    <section className="metadata-section" aria-labelledby="metadata-section-support-sla">
      <div className="metadata-section-head">
        <div className="metadata-section-head-text">
          <h2 id="metadata-section-support-sla">
            <Icon aria-hidden /> {type.label}
          </h2>
          <p className="metadata-section-desc">
            {type.description ||
              'Resolution SLA windows by priority. The support desk shows a live countdown and marks tickets Overdue when the target is missed.'}
          </p>
        </div>
      </div>

      {isLoading && <p className="metadata-inline-note">Loading SLA configuration…</p>}
      {isError && (
        <p className="metadata-inline-error" role="alert">
          {error?.response?.data?.error || error?.message || 'Failed to load SLA config'}
        </p>
      )}

      {!isLoading && !isError && (
        <form className="support-sla-form" onSubmit={handleSave}>
          <label className="metadata-checkbox-label support-sla-enable">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
            />
            Enable SLA clocks on support tickets
          </label>

          <div className="metadata-table-container">
            <table className="metadata-table support-sla-table">
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>Target</th>
                  <th>Unit</th>
                  <th>Preview</th>
                </tr>
              </thead>
              <tbody>
                {PRIORITIES.map((p) => {
                  const row = form.policies[p.id] || {};
                  return (
                    <tr key={p.id} className="metadata-row">
                      <td>
                        <strong>{p.label}</strong>
                        <div className="support-sla-hint">{p.hint}</div>
                      </td>
                      <td>
                        <input
                          type="number"
                          min={0.001}
                          step="any"
                          value={row.value ?? ''}
                          onChange={(e) => updatePolicy(p.id, 'value', e.target.value)}
                          aria-label={`${p.label} target value`}
                          disabled={!form.enabled}
                          style={{ width: '6rem' }}
                        />
                      </td>
                      <td>
                        <select
                          value={row.unit || 'days'}
                          onChange={(e) => updatePolicy(p.id, 'unit', e.target.value)}
                          aria-label={`${p.label} unit`}
                          disabled={!form.enabled}
                        >
                          {UNITS.map((u) => (
                            <option key={u.value} value={u.value}>
                              {u.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="support-sla-preview">
                        {row.value} {row.unit}
                        {String(row.unit).endsWith('s') && Number(row.value) === 1
                          ? ''
                          : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="metadata-inline-note">
            SLA starts when the ticket is created and counts down until the ticket is{' '}
            <strong>resolved</strong> or <strong>closed</strong> (clock freezes). Overdue tickets show a
            red Overdue mark and a negative clock (time past due). Changing priority recalculates the
            remaining window from creation.
          </p>

          <div className="metadata-section-actions" style={{ marginTop: '1rem' }}>
            <LoadingButton
              type="submit"
              className="btn btn-primary"
              icon={FiSave}
              loading={saveMutation.isLoading}
              loadingLabel="Saving…"
            >
              Save SLA targets
            </LoadingButton>
          </div>
        </form>
      )}
    </section>
  );
}
